/**
 * A2A Chinese Service
 * 
 * Service wrapper for A2A protocol communication with the A2AChineseAI backend.
 * Provides organized methods for all Chinese learning features:
 * - Knowledge Graph analysis
 * - Vocabulary management
 * - Writing tools
 * - DeepSeek AI capabilities
 * - UX negotiation
 */

import chineseClient from '../a2aClient';

/**
 * A2A Chinese Service Class
 * Wraps all A2A capabilities with clean, organized method names
 */
class A2AChineseService {
  constructor() {
    this.client = chineseClient;
  }

  // ============================================================================
  // Knowledge Graph Methods
  // ============================================================================

  /**
   * Analyze Chinese text and generate knowledge graph
   * @param {string} text - Chinese text to analyze
   * @param {Object} options - Analysis options
   * @param {boolean} options.extractEntities - Extract entities (default: true)
   * @param {boolean} options.extractRelations - Extract relationships (default: true)
   * @param {string} options.language - Language type ('traditional' or 'simplified')
   * @returns {Promise<Object>} Knowledge graph with nodes, links, and analysis
   */
  async analyzeKnowledgeGraph(text, options = {}) {
    const {
      extractEntities = true,
      extractRelations = true,
      language = 'traditional'
    } = options;

    return await this.client.executeTask('analyze_knowledge_graph', {
      text,
      extract_entities: extractEntities,
      extract_relations: extractRelations,
      language
    });
  }

  /**
   * Get all saved knowledge graph analyses
   * @param {string} userId - User ID (optional)
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of analyses with pagination
   */
  async getKnowledgeGraphAnalyses(userId = null, page = 1, limit = 20) {
    return await this.client.executeTask('get_kg_analyses', {
      user_id: userId,
      page,
      limit
    });
  }

  /**
   * Get specific knowledge graph analysis by ID
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Object>} Analysis with graph data
   */
  async getKnowledgeGraphAnalysis(analysisId) {
    return await this.client.executeTask('get_kg_analysis', {
      analysis_id: analysisId
    });
  }

  /**
   * Save knowledge graph analysis
   * @param {string} sampleName - Name of the sample
   * @param {string} sampleText - Original text
   * @param {Object} graphData - Graph data with nodes and links
   * @param {Object} summary - Analysis summary (optional)
   * @returns {Promise<Object>} Saved analysis
   */
  async saveKnowledgeGraphAnalysis(sampleName, sampleText, graphData, summary = null) {
    return await this.client.executeTask('save_kg_analysis', {
      sample_name: sampleName,
      sample_text: sampleText,
      graph_data: graphData,
      summary
    });
  }

  // ============================================================================
  // Vocabulary Methods
  // ============================================================================

  /**
   * Get vocabulary/phrase sets
   * @param {Object} options - Query options
   * @param {string} options.setType - 'vocabulary' or 'phrase'
   * @param {string} options.category - Category filter (optional)
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of vocabulary sets
   */
  async getVocabularySets(options = {}) {
    const {
      setType = 'vocabulary',
      category = null,
      page = 1,
      limit = 20
    } = options;

    return await this.client.executeTask('get_vocab_sets', {
      set_type: setType,
      category,
      page,
      limit
    });
  }

  /**
   * Get specific vocabulary set with all phrases
   * @param {string} setId - Vocabulary set ID
   * @returns {Promise<Object>} Vocabulary set with phrases
   */
  async getVocabularySet(setId) {
    return await this.client.executeTask('get_vocab_set', {
      set_id: setId
    });
  }

  /**
   * Get specific phrase details
   * @param {string} phraseId - Phrase ID
   * @returns {Promise<Object>} Phrase details
   */
  async getPhrase(phraseId) {
    return await this.client.executeTask('get_phrase', {
      phrase_id: phraseId
    });
  }

  /**
   * Update vocabulary learning progress
   * @param {string} userId - User ID
   * @param {string} setId - Vocabulary set ID
   * @param {string} phraseId - Phrase ID
   * @param {string} status - Progress status ('learning', 'reviewing', 'mastered')
   * @param {number} score - Score (optional)
   * @returns {Promise<Object>} Updated progress
   */
  async updateProgress(userId, setId, phraseId, status, score = null) {
    return await this.client.executeTask('update_vocab_progress', {
      user_id: userId,
      set_id: setId,
      phrase_id: phraseId,
      status,
      score
    });
  }

  /**
   * Get calendar progress for vocabulary learning
   * @param {string} userId - User ID
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @returns {Promise<Object>} Calendar data with daily progress
   */
  async getCalendarProgress(userId, year, month) {
    return await this.client.executeTask('get_vocab_calendar', {
      user_id: userId,
      year,
      month
    });
  }

  /**
   * Save vocabulary test result
   * @param {string} userId - User ID
   * @param {string} setId - Vocabulary set ID
   * @param {number} score - Score achieved
   * @param {number} totalQuestions - Total number of questions
   * @param {Array} answers - Array of answers
   * @returns {Promise<Object>} Saved test result
   */
  async saveTestResult(userId, setId, score, totalQuestions, answers) {
    return await this.client.executeTask('save_vocab_test_result', {
      user_id: userId,
      set_id: setId,
      score,
      total_questions: totalQuestions,
      answers
    });
  }

  // ============================================================================
  // Writing Tools Methods
  // ============================================================================

  /**
   * Save Chinese composition/essay
   * @param {Object} compositionData - Composition data
   * @param {string} compositionData.userId - User ID
   * @param {string} compositionData.title - Composition title
   * @param {string} compositionData.content - Composition content
   * @param {string} compositionData.style - Writing style (default: 'composition')
   * @param {number} compositionData.wordLength - Word count
   * @param {Array} compositionData.nodesData - Node data (optional)
   * @param {string} compositionData.category - Category (default: 'AIComposition')
   * @returns {Promise<Object>} Saved composition
   */
  async saveComposition(compositionData) {
    const {
      userId,
      title,
      content,
      style = 'composition',
      wordLength = 0,
      nodesData = null,
      category = 'AIComposition'
    } = compositionData;

    return await this.client.executeTask('save_composition', {
      user_id: userId,
      title,
      content,
      style,
      word_length: wordLength,
      nodes_data: nodesData,
      category
    });
  }

  /**
   * Get user compositions
   * @param {string} userId - User ID (optional)
   * @param {string} category - Category filter (optional)
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of compositions
   */
  async getCompositions(userId = null, category = null, page = 1, limit = 20) {
    return await this.client.executeTask('get_compositions', {
      user_id: userId,
      category,
      page,
      limit
    });
  }

  /**
   * Get specific composition by ID
   * @param {string} compositionId - Composition ID
   * @returns {Promise<Object>} Composition details
   */
  async getComposition(compositionId) {
    return await this.client.executeTask('get_composition', {
      composition_id: compositionId
    });
  }

  /**
   * Update composition
   * @param {string} compositionId - Composition ID
   * @param {Object} updates - Fields to update
   * @param {string} updates.title - New title (optional)
   * @param {string} updates.content - New content (optional)
   * @param {Array} updates.nodesData - New nodes data (optional)
   * @returns {Promise<Object>} Updated composition
   */
  async updateComposition(compositionId, updates) {
    const {
      title = null,
      content = null,
      nodesData = null
    } = updates;

    return await this.client.executeTask('update_composition', {
      composition_id: compositionId,
      title,
      content,
      nodes_data: nodesData
    });
  }

  /**
   * Delete composition (soft delete)
   * @param {string} compositionId - Composition ID
   * @returns {Promise<Object>} Deletion status
   */
  async deleteComposition(compositionId) {
    return await this.client.executeTask('delete_composition', {
      composition_id: compositionId
    });
  }

  /**
   * Save sentence expansion example
   * @param {string} topic - Example topic
   * @param {string} category - Category
   * @param {Array} steps - Expansion steps
   * @param {string} sourceFile - Source file (default: 'ai_generated')
   * @returns {Promise<Object>} Saved example
   */
  async saveWritingExample(topic, category, steps, sourceFile = 'ai_generated') {
    return await this.client.executeTask('save_writing_example', {
      topic,
      category,
      steps,
      source_file: sourceFile
    });
  }

  /**
   * Get sentence expansion examples by category
   * @param {string} category - Category (optional)
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of examples
   */
  async getWritingExamples(category = null, page = 1, limit = 20) {
    return await this.client.executeTask('get_writing_examples', {
      category,
      page,
      limit
    });
  }

  /**
   * Export composition as PDF
   * @param {string} compositionId - Composition ID
   * @param {string} template - PDF template (default: 'default')
   * @param {Object} options - Export options (optional)
   * @returns {Promise<Object>} PDF download URL
   */
  async exportCompositionPDF(compositionId, template = 'default', options = null) {
    return await this.client.executeTask('export_composition_pdf', {
      composition_id: compositionId,
      template,
      options
    });
  }

  // ============================================================================
  // DeepSeek AI Methods
  // ============================================================================

  /**
   * Generate Chinese essay using DeepSeek AI
   * @param {Object} essayConfig - Essay configuration
   * @param {string} essayConfig.topic - Essay topic
   * @param {string} essayConfig.essayType - Essay type ('argumentative', 'narrative', 'descriptive', 'expository')
   * @param {string} essayConfig.audience - Target audience (default: 'general')
   * @param {string} essayConfig.tone - Writing tone (default: 'formal')
   * @param {string} essayConfig.length - Essay length ('short', 'medium', 'long')
   * @returns {Promise<Object>} Generated essay with title, content, and key points
   */
  async generateEssay(essayConfig) {
    const {
      topic,
      essayType = 'argumentative',
      audience = 'general',
      tone = 'formal',
      length = 'medium'
    } = essayConfig;

    return await this.client.executeTask('generate_chinese_essay', {
      topic,
      essay_type: essayType,
      audience,
      tone,
      length
    });
  }

  /**
   * Expand Chinese sentence with context and techniques
   * @param {string} keyword - Keyword to expand
   * @param {Object} options - Expansion options
   * @param {string} options.context - Context for expansion (optional)
   * @param {string} options.expansionType - Expansion type ('descriptive', 'narrative', 'emotional')
   * @param {Array} options.techniques - Writing techniques to use (optional)
   * @returns {Promise<Object>} Expanded sentences with steps
   */
  async expandSentence(keyword, options = {}) {
    const {
      context = null,
      expansionType = 'descriptive',
      techniques = null
    } = options;

    return await this.client.executeTask('expand_chinese_sentence', {
      keyword,
      context,
      expansion_type: expansionType,
      techniques
    });
  }

  /**
   * Generate Chinese drill questions using DeepSeek AI
   * @param {Object} drillConfig - Drill configuration
   * @param {string} drillConfig.originalQuestion - Original question to base drills on
   * @param {string} drillConfig.questionType - Question type ('vocabulary', 'grammar', 'comprehension', 'writing')
   * @param {string} drillConfig.difficulty - Difficulty level ('beginner', 'intermediate', 'advanced')
   * @param {string} drillConfig.grade - Grade level (optional)
   * @param {number} drillConfig.numberOfDrills - Number of drill questions to generate (default: 3)
   * @returns {Promise<Object>} Generated drill questions with answers and explanations
   */
  async generateDrillQuestions(drillConfig) {
    const {
      originalQuestion,
      questionType = 'vocabulary',
      difficulty = 'intermediate',
      grade = null,
      numberOfDrills = 3
    } = drillConfig;

    return await this.client.executeTask('generate_chinese_drill_questions', {
      original_question: originalQuestion,
      question_type: questionType,
      difficulty,
      grade,
      number_of_drills: numberOfDrills
    });
  }

  // ============================================================================
  // UX Negotiation Methods
  // ============================================================================

  /**
   * Negotiate optimal UX format based on user context
   * @param {Object} context - User context (optional, auto-detected if not provided)
   * @param {string} context.device - Device type ('mobile', 'tablet', 'desktop')
   * @param {number} context.screenWidth - Screen width in pixels
   * @param {number} context.screenHeight - Screen height in pixels
   * @param {string} context.bandwidth - Network bandwidth ('2g', '3g', '4g', '5g', 'wifi')
   * @param {string} context.userLevel - User level ('beginner', 'intermediate', 'expert')
   * @param {string} context.preferredLanguage - Preferred language ('traditional', 'simplified')
   * @returns {Promise<Object>} UX format recommendations
   */
  async negotiateUX(context = null) {
    // Auto-detect context if not provided
    const userContext = context || this.detectContext();
    
    return await this.client.negotiateUX(userContext);
  }

  /**
   * Detect current user context
   * @returns {Object} Detected user context
   */
  detectContext() {
    return this.client.detectContext();
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Get all available capabilities
   * @returns {Array|null} Array of capabilities
   */
  getCapabilities() {
    return this.client.getCapabilities();
  }

  /**
   * Check if a specific capability is available
   * @param {string} capabilityName - Capability name
   * @returns {Promise<boolean>} True if capability exists
   */
  async hasCapability(capabilityName) {
    return await this.client.hasCapability(capabilityName);
  }

  /**
   * Get rate limit information
   * @returns {Object} Rate limit info with remaining requests and reset time
   */
  getRateLimitInfo() {
    return this.client.getRateLimitInfo();
  }

  /**
   * Clear cached capabilities (force refresh)
   */
  clearCache() {
    this.client.clearCache();
  }

  /**
   * Cleanup - unsubscribe from all tasks
   */
  cleanup() {
    this.client.cleanup();
  }

  // ============================================================================
  // HKDSE Methods
  // ============================================================================

  /**
   * Get HKDSE original Chinese texts
   * @param {number} year - Year (optional)
   * @param {string} category - Category (optional)
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of HKDSE texts
   */
  async getHKDSETexts(year = null, category = null, page = 1, limit = 20) {
    return await this.client.executeTask('get_hkdse_texts', {
      year,
      category,
      page,
      limit
    });
  }

  /**
   * Get HKDSE original text by ID
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Original text data
   */
  async getHKDSEOriginalText(textId) {
    return await this.client.executeTask('get_hkdse_original_text', {
      text_id: textId
    });
  }

  /**
   * Get HKDSE quiz by text ID
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Quiz data
   */
  async getHKDSEQuiz(textId) {
    return await this.client.executeTask('get_hkdse_quiz', {
      text_id: textId
    });
  }

  /**
   * Get HKDSE flash cards by text ID
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Flash cards data
   */
  async getHKDSEFlashCards(textId) {
    return await this.client.executeTask('get_hkdse_flash_cards', {
      text_id: textId
    });
  }

  /**
   * Get HKDSE Q&A by text ID
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Q&A data
   */
  async getHKDSEQA(textId) {
    return await this.client.executeTask('get_hkdse_qa', {
      text_id: textId
    });
  }

  /**
   * Get primary Chinese data for all grades
   * @returns {Promise<Object>} Primary Chinese data organized by grade
   */
  async getPrimaryChineseData() {
    return await this.client.executeTask('get_primary_chinese_data', {});
  }
}

/**
 * Create and export singleton instance
 */
const a2aChineseService = new A2AChineseService();

export default a2aChineseService;

/**
 * Export class for testing or custom instances
 */
export { A2AChineseService };

